// Wall-clock helpers for a named time zone (the business's), independent of
// the viewer's device zone. Bahrain has no DST; others are handled by
// re-deriving the offset.

export interface Wall {
  y: number;
  m: number; // 1-12
  d: number;
  h: number;
  min: number;
  wd: number; // 0 = Sunday
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string) {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", weekday: "short", hourCycle: "h23" });
    fmtCache.set(tz, f);
  }
  return f;
}

const weekdays: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** wall returns the date and time a moment shows in tz. */
export function wall(date: Date, tz: string): Wall {
  const p: Record<string, string> = {};
  for (const part of fmt(tz).formatToParts(date)) p[part.type] = part.value;
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, min: +p.minute, wd: weekdays[p.weekday] };
}

/** zoned returns the moment when tz's clock shows the given wall time. */
export function zoned(y: number, m: number, d: number, h: number, min: number, tz: string): Date {
  const guess = Date.UTC(y, m - 1, d, h, min);
  const w = wall(new Date(guess), tz);
  const offset = Date.UTC(w.y, w.m - 1, w.d, w.h, w.min) - guess;
  return new Date(guess - offset);
}

/** dayKey is YYYY-MM-DD in tz. */
export function dayKey(date: Date, tz: string): string {
  const w = wall(date, tz);
  return `${w.y}-${String(w.m).padStart(2, "0")}-${String(w.d).padStart(2, "0")}`;
}

/** addDays moves a YYYY-MM-DD key by n calendar days. */
export function addDays(key: string, n: number): string {
  const [y, m, d] = key.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

export function startOf(key: string, tz: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return zoned(y, m, d, 0, 0, tz);
}

export function weekdayOf(key: string): number {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function fmtTime(date: Date, tz: string): string {
  return date.toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit" });
}

export function fmtDay(key: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" }): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-GB", { ...opts, timeZone: "UTC" });
}

export const minutesLabel = (min: number) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
